import { createElement, forwardRef, type ForwardRefExoticComponent, type RefAttributes, type SVGProps } from 'react';
import { portalSymbol } from './portal-symbols';

export type LucideProps = SVGProps<SVGSVGElement> & {
  absoluteStrokeWidth?: boolean;
  size?: number | string;
};

export type LucideIcon = ForwardRefExoticComponent<LucideProps & RefAttributes<SVGSVGElement>>;

function iconSlug(name: string) {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

function makeIcon(name: string): LucideIcon {
  const slug = iconSlug(name);
  const layered = portalSymbol(name);
  const Icon = forwardRef<SVGSVGElement, LucideProps>(({
    absoluteStrokeWidth,
    children,
    className,
    color = 'currentColor',
    size = 24,
    strokeWidth = 2,
    ...props
  }, ref) => createElement('svg', {
    ...props,
    ref,
    xmlns: 'http://www.w3.org/2000/svg',
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: color,
    color,
    strokeWidth: absoluteStrokeWidth && Number(size) ? Number(strokeWidth) * 24 / Number(size) : strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className: ['lucide', `lucide-${slug}`, className].filter(Boolean).join(' '),
  }, createElement('use', { href: `/icons/lucide.svg#${name}`, className: layered ? 'portal-icon-outline' : undefined }), layered, children));
  Icon.displayName = name;
  return Icon;
}

function makeLayeredTicketIcon(): LucideIcon {
  const Icon = forwardRef<SVGSVGElement, LucideProps>(({
    absoluteStrokeWidth,
    children,
    className,
    color = 'currentColor',
    size = 24,
    strokeWidth = 2,
    ...props
  }, ref) => createElement('svg', {
    ...props,
    ref,
    xmlns: 'http://www.w3.org/2000/svg',
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: color,
    strokeWidth: absoluteStrokeWidth && Number(size) ? Number(strokeWidth) * 24 / Number(size) : strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className: ['lucide', 'lucide-ticket', className].filter(Boolean).join(' '),
  },
  createElement('path', {
    className: 'portal-icon-layer',
    d: 'M4.25 4.5h15.5A2.25 2.25 0 0 1 22 6.75v2.1a3.4 3.4 0 0 0 0 6.3v2.1a2.25 2.25 0 0 1-2.25 2.25H4.25A2.25 2.25 0 0 1 2 17.25v-2.1a3.4 3.4 0 0 0 0-6.3v-2.1A2.25 2.25 0 0 1 4.25 4.5Z',
    fill: color,
    opacity: 0.12,
    stroke: 'none',
  }),
  createElement('path', {
    className: 'symbol-body',
    d: 'M4.25 4.5h15.5A2.25 2.25 0 0 1 22 6.75v2.1a3.4 3.4 0 0 0 0 6.3v2.1a2.25 2.25 0 0 1-2.25 2.25H4.25A2.25 2.25 0 0 1 2 17.25v-2.1a3.4 3.4 0 0 0 0-6.3v-2.1A2.25 2.25 0 0 1 4.25 4.5Z',
  }),
  createElement('path', {
    className: 'symbol-detail',
    d: 'M13.25 4.5v2.1m0 3.05v1.3m0 3.05v1.3m0 2.1v2.1',
  }),
  children));
  Icon.displayName = 'Ticket';
  return Icon;
}

export const Activity = /* @__PURE__ */ makeIcon('Activity');
export const AlertCircle = /* @__PURE__ */ makeIcon('AlertCircle');
export const AlertTriangle = /* @__PURE__ */ makeIcon('AlertTriangle');
export const Archive = /* @__PURE__ */ makeIcon('Archive');
export const ArchiveRestore = /* @__PURE__ */ makeIcon('ArchiveRestore');
export const ArrowDown = /* @__PURE__ */ makeIcon('ArrowDown');
export const ArrowDownLeft = /* @__PURE__ */ makeIcon('ArrowDownLeft');
export const ArrowLeft = /* @__PURE__ */ makeIcon('ArrowLeft');
export const ArrowRight = /* @__PURE__ */ makeIcon('ArrowRight');
export const ArrowUp = /* @__PURE__ */ makeIcon('ArrowUp');
export const ArrowUpRight = /* @__PURE__ */ makeIcon('ArrowUpRight');
export const AudioLines = /* @__PURE__ */ makeIcon('AudioLines');
export const Ban = /* @__PURE__ */ makeIcon('Ban');
export const BarChart3 = /* @__PURE__ */ makeIcon('BarChart3');
export const Bell = /* @__PURE__ */ makeIcon('Bell');
export const BellRing = /* @__PURE__ */ makeIcon('BellRing');
export const BookOpen = /* @__PURE__ */ makeIcon('BookOpen');
export const Braces = /* @__PURE__ */ makeIcon('Braces');
export const BriefcaseBusiness = /* @__PURE__ */ makeIcon('BriefcaseBusiness');
export const Building2 = /* @__PURE__ */ makeIcon('Building2');
export const CalendarClock = /* @__PURE__ */ makeIcon('CalendarClock');
export const Check = /* @__PURE__ */ makeIcon('Check');
export const CheckCircle2 = /* @__PURE__ */ makeIcon('CheckCircle2');
export const ChevronDown = /* @__PURE__ */ makeIcon('ChevronDown');
export const ChevronLeft = /* @__PURE__ */ makeIcon('ChevronLeft');
export const ChevronRight = /* @__PURE__ */ makeIcon('ChevronRight');
export const ChevronUp = /* @__PURE__ */ makeIcon('ChevronUp');
export const Circle = /* @__PURE__ */ makeIcon('Circle');
export const CircleAlert = /* @__PURE__ */ makeIcon('CircleAlert');
export const CircleDollarSign = /* @__PURE__ */ makeIcon('CircleDollarSign');
export const CircleDot = /* @__PURE__ */ makeIcon('CircleDot');
export const CircleHelp = /* @__PURE__ */ makeIcon('CircleHelp');
export const CircleOff = /* @__PURE__ */ makeIcon('CircleOff');
export const Clock3 = /* @__PURE__ */ makeIcon('Clock3');
export const Cloud = /* @__PURE__ */ makeIcon('Cloud');
export const CloudCog = /* @__PURE__ */ makeIcon('CloudCog');
export const Columns3 = /* @__PURE__ */ makeIcon('Columns3');
export const Copy = /* @__PURE__ */ makeIcon('Copy');
export const Database = /* @__PURE__ */ makeIcon('Database');
export const Delete = /* @__PURE__ */ makeIcon('Delete');
export const Download = /* @__PURE__ */ makeIcon('Download');
export const ExternalLink = /* @__PURE__ */ makeIcon('ExternalLink');
export const Eye = /* @__PURE__ */ makeIcon('Eye');
export const EyeOff = /* @__PURE__ */ makeIcon('EyeOff');
export const FileSearch = /* @__PURE__ */ makeIcon('FileSearch');
export const FileText = /* @__PURE__ */ makeIcon('FileText');
export const Filter = /* @__PURE__ */ makeIcon('Filter');
export const Gauge = /* @__PURE__ */ makeIcon('Gauge');
export const GitMerge = /* @__PURE__ */ makeIcon('GitMerge');
export const GripVertical = /* @__PURE__ */ makeIcon('GripVertical');
export const HardDrive = /* @__PURE__ */ makeIcon('HardDrive');
export const Headphones = /* @__PURE__ */ makeIcon('Headphones');
export const History = /* @__PURE__ */ makeIcon('History');
export const Inbox = /* @__PURE__ */ makeIcon('Inbox');
export const KeyRound = /* @__PURE__ */ makeIcon('KeyRound');
export const Layers3 = /* @__PURE__ */ makeIcon('Layers3');
export const LayoutDashboard = /* @__PURE__ */ makeIcon('LayoutDashboard');
export const Library = /* @__PURE__ */ makeIcon('Library');
export const Link2 = /* @__PURE__ */ makeIcon('Link2');
export const List = /* @__PURE__ */ makeIcon('List');
export const ListChecks = /* @__PURE__ */ makeIcon('ListChecks');
export const LoaderCircle = /* @__PURE__ */ makeIcon('LoaderCircle');
export const LockKeyhole = /* @__PURE__ */ makeIcon('LockKeyhole');
export const LogOut = /* @__PURE__ */ makeIcon('LogOut');
export const Mail = /* @__PURE__ */ makeIcon('Mail');
export const Menu = /* @__PURE__ */ makeIcon('Menu');
export const MessageCircle = /* @__PURE__ */ makeIcon('MessageCircle');
export const MessageSquare = /* @__PURE__ */ makeIcon('MessageSquare');
export const MessageSquareText = /* @__PURE__ */ makeIcon('MessageSquareText');
export const MessagesSquare = /* @__PURE__ */ makeIcon('MessagesSquare');
export const Mic = /* @__PURE__ */ makeIcon('Mic');
export const MicOff = /* @__PURE__ */ makeIcon('MicOff');
export const Minus = /* @__PURE__ */ makeIcon('Minus');
export const Moon = /* @__PURE__ */ makeIcon('Moon');
export const MoreHorizontal = /* @__PURE__ */ makeIcon('MoreHorizontal');
export const Network = /* @__PURE__ */ makeIcon('Network');
export const Paperclip = /* @__PURE__ */ makeIcon('Paperclip');
export const Pause = /* @__PURE__ */ makeIcon('Pause');
export const Pencil = /* @__PURE__ */ makeIcon('Pencil');
export const PencilLine = /* @__PURE__ */ makeIcon('PencilLine');
export const Phone = /* @__PURE__ */ makeIcon('Phone');
export const PhoneCall = /* @__PURE__ */ makeIcon('PhoneCall');
export const PhoneForwarded = /* @__PURE__ */ makeIcon('PhoneForwarded');
export const PhoneIncoming = /* @__PURE__ */ makeIcon('PhoneIncoming');
export const PhoneMissed = /* @__PURE__ */ makeIcon('PhoneMissed');
export const PhoneOff = /* @__PURE__ */ makeIcon('PhoneOff');
export const PhoneOutgoing = /* @__PURE__ */ makeIcon('PhoneOutgoing');
export const Play = /* @__PURE__ */ makeIcon('Play');
export const Plus = /* @__PURE__ */ makeIcon('Plus');
export const QrCode = /* @__PURE__ */ makeIcon('QrCode');
export const RefreshCw = /* @__PURE__ */ makeIcon('RefreshCw');
export const RotateCcw = /* @__PURE__ */ makeIcon('RotateCcw');
export const RotateCw = /* @__PURE__ */ makeIcon('RotateCw');
export const Save = /* @__PURE__ */ makeIcon('Save');
export const ScanSearch = /* @__PURE__ */ makeIcon('ScanSearch');
export const Search = /* @__PURE__ */ makeIcon('Search');
export const Send = /* @__PURE__ */ makeIcon('Send');
export const Server = /* @__PURE__ */ makeIcon('Server');
export const Settings = /* @__PURE__ */ makeIcon('Settings');
export const ShieldCheck = /* @__PURE__ */ makeIcon('ShieldCheck');
export const ShieldOff = /* @__PURE__ */ makeIcon('ShieldOff');
export const Smile = /* @__PURE__ */ makeIcon('Smile');
export const SmilePlus = /* @__PURE__ */ makeIcon('SmilePlus');
export const Sparkles = /* @__PURE__ */ makeIcon('Sparkles');
export const Square = /* @__PURE__ */ makeIcon('Square');
export const Star = /* @__PURE__ */ makeIcon('Star');
export const Sun = /* @__PURE__ */ makeIcon('Sun');
export const Tag = /* @__PURE__ */ makeIcon('Tag');
export const Ticket = /* @__PURE__ */ makeLayeredTicketIcon();
export const Timer = /* @__PURE__ */ makeIcon('Timer');
export const Trash2 = /* @__PURE__ */ makeIcon('Trash2');
export const Unlock = /* @__PURE__ */ makeIcon('Unlock');
export const Upload = /* @__PURE__ */ makeIcon('Upload');
export const UserPlus = /* @__PURE__ */ makeIcon('UserPlus');
export const UserRound = /* @__PURE__ */ makeIcon('UserRound');
export const Users = /* @__PURE__ */ makeIcon('Users');
export const Volume2 = /* @__PURE__ */ makeIcon('Volume2');
export const VolumeX = /* @__PURE__ */ makeIcon('VolumeX');
export const Workflow = /* @__PURE__ */ makeIcon('Workflow');
export const Wrench = /* @__PURE__ */ makeIcon('Wrench');
export const X = /* @__PURE__ */ makeIcon('X');
export const Zap = /* @__PURE__ */ makeIcon('Zap');
export const ZoomIn = /* @__PURE__ */ makeIcon('ZoomIn');
export const ZoomOut = /* @__PURE__ */ makeIcon('ZoomOut');
